import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import PageContainer from '../common/PageContainer';
import './Auth.css';

interface User {
  _id: string;
  name: string;
  email: string;
  createdAt: string;
  lastLogin: string;
}

interface ImportStats {
  added: number;
  updated: number;
  skipped: number;
}

interface Invite {
  id: string;
  code: string;
  used: boolean;
  createdAt: string;
  expiresAt: string;
  usedBy?: string;
}

const ProfilePage: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [importStats, setImportStats] = useState<ImportStats | null>(null);
  const [importSuccess, setImportSuccess] = useState<boolean>(false);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [inviteGenerated, setInviteGenerated] = useState<boolean>(false);
  const [newInvite, setNewInvite] = useState<Invite | null>(null);
  const [tags, setTags] = useState<string>('');
  const [showImportOptions, setShowImportOptions] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const loadUserProfile = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/');
        return;
      }

      try {
        const response = await fetch('/api/auth/me', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to load profile');
        }

        const userData = await response.json();
        setUser(userData);
        
        // Load user's invites
        loadInvites();
      } catch (error) {
        setError('Failed to load user profile');
        console.error('Profile loading error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadUserProfile();
  }, [navigate]);
  
  const loadInvites = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    try {
      const response = await fetch('/api/auth/invites', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to load invites');
      }
      
      const invitesData = await response.json();
      setInvites(invitesData);
    } catch (error) {
      console.error('Error loading invites:', error);
    }
  };
  
  const handleGenerateInvite = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    try {
      setError('');
      const response = await fetch('/api/auth/invites/generate', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to generate invite code');
      }
      
      const data = await response.json();
      setNewInvite(data.invite);
      setInviteGenerated(true);
      
      // Refresh the invites list
      loadInvites();
    } catch (error) {
      setError('Failed to generate invite code');
      console.error('Generate invite error:', error);
    }
  };

  const handleExport = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const response = await fetch('/api/auth/export-vocabulary', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to export vocabulary');
      }

      // Get the CSV content
      const csvContent = await response.text();

      // Create a blob and download link
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.setAttribute('hidden', '');
      a.setAttribute('href', url);
      a.setAttribute('download', 'vocabulary.csv');
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (error) {
      setError('Failed to export vocabulary');
      console.error('Export error:', error);
    }
  };

  const handleImportClick = () => {
    setShowImportOptions(true);
  };

  const handleCancelImport = () => {
    setShowImportOptions(false);
    setTags('');
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const token = localStorage.getItem('token');
    if (!token) return;

    // Reset states
    setImportStats(null);
    setImportSuccess(false);
    setError('');

    try {
      // Read file content
      const reader = new FileReader();
      reader.onload = async (e) => {
        const csv = e.target?.result as string;
        
        // Send to API
        const response = await fetch('/api/auth/import-vocabulary', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ 
            csv,
            tags
          })
        });

        if (!response.ok) {
          throw new Error('Failed to import vocabulary');
        }

        const result = await response.json();
        setImportSuccess(true);
        setImportStats(result.stats);
        setShowImportOptions(false);
        setTags('');
      };
      
      reader.readAsText(file);
    } catch (error) {
      setError('Failed to import vocabulary');
      console.error('Import error:', error);
    } finally {
      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    
    // Dispatch a custom event to notify other components
    window.dispatchEvent(new Event('logout'));
    
    // Navigate to home page
    navigate('/');
  };

  if (isLoading) {
    return (
      <PageContainer>
        <div className="auth-container">
          <div className="loading-spinner"></div>
        </div>
      </PageContainer>
    );
  }

  if (!user) {
    return (
      <PageContainer>
        <div className="auth-container">
          <div className="auth-box">
            <h2>Profile Not Available</h2>
            <p>Please log in to view your profile.</p>
            <button className="auth-button" onClick={() => navigate('/')}>
              Go to Home
            </button>
          </div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="auth-container">
        <h2>User Profile</h2>
        
        {error && <div className="auth-error">{error}</div>}
        
        <div className="profile-info">
          <p><strong>Name:</strong> {user.name}</p>
          <p><strong>Email:</strong> {user.email}</p>
          <p><strong>Joined:</strong> {new Date(user.createdAt).toLocaleDateString()}</p>
          <p><strong>Last Login:</strong> {new Date(user.lastLogin).toLocaleDateString()}</p>
        </div>
        
        <div className="profile-account-section">
          <h3>Account Actions</h3>
          <div className="profile-actions">
            <button className="auth-button logout-button" onClick={handleLogout}>
              Logout
            </button>
          </div>
        </div>
        
        <div className="profile-vocabulary-section">
          <h3>Vocabulary Management</h3>
          
          <div className="profile-actions">
            <button className="auth-button vocabulary-button" onClick={handleExport}>
              Export Vocabulary
            </button>
            
            <button className="auth-button vocabulary-button" onClick={handleImportClick}>
              Import Vocabulary
            </button>
          </div>
          
          {showImportOptions && (
            <div className="import-options">
              <h4>Import Options</h4>
              
              <div className="form-group">
                <label htmlFor="tags">Tags (comma separated)</label>
                <input
                  type="text"
                  id="tags"
                  value={tags}
                  onChange={(e) => setTags(e.target.value)}
                  placeholder="E.g., chapter1, common-words, difficult"
                />
                <p className="field-help">These tags will be applied to all imported words</p>
              </div>
              
              <div className="import-buttons">
                <button className="cancel-button" onClick={handleCancelImport}>
                  Cancel
                </button>
                
                <label className="file-upload-button">
                  Upload CSV File
                  <input 
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    accept=".csv"
                    onChange={handleFileUpload}
                  />
                </label>
              </div>
              
              <div className="import-instructions">
                <p>CSV file should have a <strong>word</strong> column.</p>
                <p>Example: <code>word</code></p>
                <p>The first line should be the header.</p>
              </div>
            </div>
          )}
          
          {importSuccess && importStats && (
            <div className="import-results">
              <h4>Import Results</h4>
              <p><strong>Words Added:</strong> {importStats.added}</p>
              <p><strong>Words Updated:</strong> {importStats.updated}</p>
              <p><strong>Words Skipped (already exist):</strong> {importStats.skipped}</p>
              {tags && <p><strong>Tags Applied:</strong> {tags}</p>}
            </div>
          )}
        </div>
        
        <div className="profile-invites-section">
          <h3>Invitation Management</h3>
          
          <div className="profile-actions">
            <button className="auth-button invite-button" onClick={handleGenerateInvite}>
              Generate New Invite Code
            </button>
          </div>
          
          {inviteGenerated && newInvite && (
            <div className="invite-generated">
              <h4>New Invite Code Generated</h4>
              <p className="invite-code">{newInvite.code}</p>
              <p>Expires on: {new Date(newInvite.expiresAt).toLocaleDateString()}</p>
              <p className="invite-instructions">Share this code with someone to invite them to the platform.</p>
            </div>
          )}
          
          {invites.length > 0 && (
            <div className="invites-list">
              <h4>Your Invite Codes</h4>
              <table className="invites-table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Status</th>
                    <th>Expires On</th>
                  </tr>
                </thead>
                <tbody>
                  {invites.map(invite => (
                    <tr key={invite.id} className={invite.used ? 'invite-used' : 'invite-active'}>
                      <td>{invite.code}</td>
                      <td>{invite.used ? 'Used' : 'Active'}</td>
                      <td>{new Date(invite.expiresAt).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </PageContainer>
  );
};

export default ProfilePage; 